import { stringify } from 'querystring'

import { useRouter } from 'next/router'
import React from 'react'
import Head from 'next/head'
import { useIntersectionObserver } from 'react-intersection-observer-hook'
import { useQueryClient } from 'react-query'

import {
  HeaderBar,
  HeaderBarLeftAction,
  HeaderBarTitle,
} from '@wartek-id/header-bar'
import { Icon } from '@wartek-id/icon'
import { Shimmer } from '@wartek-id/shimmer'
import { historyUtils } from '@wartek-id/fe-toolbox'
import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text'

import { useGetAksiNyataList } from 'api/microlearning/useAPIAksiNyata'
import { EmptyState, EmptyStateAction } from 'components/EmptyState'
import AksiNyataListItem from 'app/PelatihanMandiriPage/partials/AksiNyata/AksiNyataListItem'
import TabbedNav from 'app/PelatihanMandiriPage/components/TabbedNav'
import TopicListCallToActionRevamp from 'app/PelatihanMandiriPage/partials/AksiNyata/TopicListCallToActionRevamp'

const DaftarAksiNyataPage = () => {
  const aksiNyataFeed = useGetAksiNyataList()
  const router = useRouter()
  const [ref, { entry }] = useIntersectionObserver()
  const isVisible = entry && entry.isIntersecting
  const queryClient = useQueryClient()

  const resetFeed = () => {
    queryClient.resetQueries(['aksinyata-infinite-query'])
  }

  React.useEffect(() => {
    if (
      isVisible &&
      !aksiNyataFeed.isFetchingNextPage &&
      aksiNyataFeed.hasNextPage
    ) {
      // eslint-disable-next-line no-console
      aksiNyataFeed.fetchNextPage()
    }
  }, [aksiNyataFeed, isVisible])

  return (
    <>
      <Head>
        <title>Pelatihan Mandiri | Daftar Aksi Nyata</title>
      </Head>
      <TabbedNav activeMenu="aksi-nyata" />
      <div
        className="bg-white"
        style={{
          height: 'calc(100vh - 3.5rem)',
        }}
      >
        <div className="pt-9">
          <div className="px-4">
            <TopicListCallToActionRevamp />
          </div>
          {aksiNyataFeed.status === 'loading' &&
            [...Array(4)].map((_, index) => (
              <div key={index} className="px-4">
                <div className="mb-4 border-b border-subdued pb-4">
                  <Shimmer
                    height={10}
                    noOfLines={2}
                    className="mb-4"
                    style={{ width: '100%' }}
                  />
                  <div className="flex items-center">
                    <Shimmer
                      className="mr-2"
                      rounded={true}
                      height={32}
                      width={32}
                    />
                    <Shimmer
                      height={10}
                      noOfLines={1}
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>
              </div>
            ))}
          {aksiNyataFeed.status === 'success' &&
            aksiNyataFeed.data.pages.length == 0 && (
              <EmptyState
                title="Belum ada Aksi Nyata yang dikirimkan saat ini"
                description="Kembali"
              >
                <EmptyStateAction onClick={() => router.back()}>
                  Muat Ulang
                </EmptyStateAction>
              </EmptyState>
            )}
          <div>
            {!aksiNyataFeed.hasNextPage && (
              <div className="p-4">
                <Button type="button" fullWidth size="sm" onClick={resetFeed}>
                  <Icon
                    className="mr-2"
                    as="i"
                    color="inverse"
                    fontSize="small"
                  >
                    refresh
                  </Icon>
                  Perbaharui daftar aksi nyata
                </Button>
              </div>
            )}
            {aksiNyataFeed.status === 'success' &&
              aksiNyataFeed.data.pages.length > 0 &&
              aksiNyataFeed.data.pages.map((page, index) => (
                <React.Fragment key={index}>
                  {page.data.map(
                    ({ id, title, creator, topicId, topicName }, index) => (
                      <AksiNyataListItem
                        key={index}
                        href={
                          `aksi-nyata/${id}?` +
                          stringify({
                            topik_name: topicName,
                            topik_id: topicId,
                          })
                        }
                        title={title}
                        avatarUrl={creator.avatarUrl}
                        author={creator.name}
                        email={creator.email}
                        topicId={topicId}
                        topicName={topicName}
                        order={index}
                        aksiNyataId={id as string}
                      />
                    )
                  )}
                </React.Fragment>
              ))}
          </div>
          {aksiNyataFeed.hasNextPage && (
            <div className="p-4">
              <Button
                isLoading={
                  aksiNyataFeed.isFetching || aksiNyataFeed.isFetchingNextPage
                }
                size="lg"
                color="black"
                fullWidth
                ref={ref}
                onClick={() => aksiNyataFeed.fetchNextPage()}
              >
                Selanjutnya
              </Button>
            </div>
          )}
          {!aksiNyataFeed.hasNextPage && (
            <div className="p-4">
              <div className="mb-4 border border-subdued rounded-lg bg-surface-subdued overflow-hidden">
                <div className="flex items-center px-4 py-2">
                  <Icon
                    className="mr-2"
                    as="i"
                    color="default"
                    fontSize="small"
                  >
                    error
                  </Icon>
                  <Text variant="body-sm" as="span">
                    <strong>Anda telah melihat semua Aksi Nyata</strong>
                  </Text>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

const PageHeader = () => {
  const router = useRouter()

  return (
    <HeaderBar borderless fixed className="z-10">
      <HeaderBarLeftAction
        icon={
          <Icon as="i" color="default">
            arrow_back
          </Icon>
        }
        onClick={() => historyUtils.backToApp(router.back)}
      />
      <HeaderBarTitle>Aksi Nyata Rekan Guru</HeaderBarTitle>
    </HeaderBar>
  )
}

DaftarAksiNyataPage.layoutOpts = {
  hasHeader: true,
  className: 'text-default bg-gray-5',
  header: PageHeader,
}

export default DaftarAksiNyataPage
